import { encodeLogoRaster1Bit } from '../escpos/rasterLogo';
import { formatThermalMoney } from '../formatThermalMoney';
import { fitLineToWidth, getReceiptLineWidthForModel, getReceiptRasterDotsPerLine } from '../receiptLayout';
import {
    layoutReceiptForPreview,
    type ReceiptPreviewLine,
} from '../receiptPreviewLayout';
import type { ReceiptLineItem, ReceiptPayload } from '../types';
import { getXpP203aLabelConfig } from '../xpP203aLabelConfig';
import { buildTsplJob, buildTsplLabelHeaderLines, tsplBitmapPart, tsplQrLine, tsplTextLine } from './tsplEncode';
import type { TsplJobPart } from './tsplEncode';
import {
    DOTS_PER_MM,
    LABEL_BOTTOM_MARGIN_DOTS,
    LABEL_TOP_Y,
    paginateDrawBlocks,
    type TsplDrawBlock,
} from './tsplLabelPagination';

const MARGIN_X = 8;
const SPACER_DOTS = 10;
const LOGO_GAP_DOTS = 8;
const LINE_GAP = 4;
const QR_CELL = 4;
const QR_CAPTION_FONT = '1';

type TsplFontStyle = {
  font: string;
  xMul: number;
  yMul: number;
  lineHeight: number;
  centered: boolean;
};

const STYLE: Record<ReceiptPreviewLine['kind'], TsplFontStyle | null> = {
  logo: null,
  branding: { font: '2', xMul: 1, yMul: 1, lineHeight: 20, centered: true },
  title: { font: '3', xMul: 1, yMul: 1, lineHeight: 28, centered: true },
  text: { font: '2', xMul: 1, yMul: 1, lineHeight: 20, centered: false },
  meta: { font: '2', xMul: 1, yMul: 1, lineHeight: 20, centered: false },
  item: { font: '2', xMul: 1, yMul: 1, lineHeight: 20, centered: false },
  total: { font: '3', xMul: 1, yMul: 1, lineHeight: 24, centered: false },
  qr: null,
  spacer: null,
};

function charWidthDots(font: string, xMul: number): number {
  const base: Record<string, number> = { '1': 8, '2': 12, '3': 16, '4': 24 };
  return (base[font] ?? 12) * xMul;
}

function textWidthDots(text: string, font: string, xMul: number): number {
  return text.length * charWidthDots(font, xMul);
}

function textXCentered(
  text: string,
  style: TsplFontStyle,
  dotsPerLine: number,
  lineWidth: number,
): number {
  const fitted = fitLineToWidth(text, lineWidth);
  const est = textWidthDots(fitted, style.font, style.xMul);
  return Math.max(MARGIN_X, Math.floor((dotsPerLine - est) / 2));
}

function textXRight(text: string, font: string, xMul: number, dotsPerLine: number): number {
  const w = textWidthDots(text, font, xMul);
  return Math.max(MARGIN_X, dotsPerLine - MARGIN_X - w);
}

function advanceStyleHeight(style: TsplFontStyle): number {
  return style.lineHeight * style.yMul + LINE_GAP;
}

function estimateQrBlockDots(payload: string): number {
  const modules = Math.min(41, Math.max(21, Math.ceil(payload.length / 2) + 17));
  return modules * QR_CELL;
}

function spacerBlock(): TsplDrawBlock {
  return { heightDots: SPACER_DOTS, render: () => [] };
}

function singleTextBlock(
  text: string,
  style: TsplFontStyle,
  dotsPerLine: number,
  lineWidth: number,
): TsplDrawBlock {
  const fitted = fitLineToWidth(text, lineWidth);
  const height = fitted ? advanceStyleHeight(style) : 0;
  return {
    heightDots: height,
    render: (y) => {
      if (!fitted) return [];
      const x = style.centered
        ? textXCentered(fitted, style, dotsPerLine, lineWidth)
        : MARGIN_X;
      return [
        tsplTextLine(x, y, fitted, {
          font: style.font,
          xMul: style.xMul,
          yMul: style.yMul,
        }),
      ];
    },
  };
}

function dualColumnBlock(
  left: string,
  right: string,
  style: TsplFontStyle,
  dotsPerLine: number,
  lineWidth: number,
): TsplDrawBlock {
  const leftFit = fitLineToWidth(left.trim(), lineWidth);
  const rightRaw = right.trim();
  const maxRightWidth = Math.floor((dotsPerLine - MARGIN_X * 2) * 0.55);
  const rightChars = Math.max(8, Math.floor(maxRightWidth / charWidthDots(style.font, style.xMul)));
  const rightFit = fitLineToWidth(rightRaw, rightChars);
  const wrapFont = '1';
  const wrapLineHeight = 14;
  const rightTooWide =
    rightRaw.length > 0 &&
    textWidthDots(rightRaw, style.font, style.xMul) > maxRightWidth;

  let height = advanceStyleHeight(style);
  let wrappedRight = '';
  if (rightTooWide && rightRaw.length > rightFit.length) {
    wrappedRight = fitLineToWidth(rightRaw.slice(rightFit.length), lineWidth);
    height += wrapLineHeight + LINE_GAP;
  }

  return {
    heightDots: height,
    render: (y) => {
      const cmds: string[] = [];
      if (leftFit) {
        cmds.push(
          tsplTextLine(MARGIN_X, y, leftFit, {
            font: style.font,
            xMul: style.xMul,
            yMul: style.yMul,
          }),
        );
      }
      if (rightFit) {
        cmds.push(
          tsplTextLine(
            textXRight(rightFit, style.font, style.xMul, dotsPerLine),
            y,
            rightFit,
            { font: style.font, xMul: style.xMul, yMul: style.yMul },
          ),
        );
      }
      if (wrappedRight) {
        const y2 = y + style.lineHeight + LINE_GAP;
        cmds.push(
          tsplTextLine(MARGIN_X, y2, wrappedRight, {
            font: wrapFont,
            xMul: 1,
            yMul: 1,
          }),
        );
      }
      return cmds;
    },
  };
}

function itemDualColumnBlock(
  item: ReceiptLineItem,
  currency: string,
  style: TsplFontStyle,
  dotsPerLine: number,
  lineWidth: number,
): TsplDrawBlock {
  const left = item.qty != null ? `${item.label} x${item.qty}` : item.label;
  const right =
    item.total != null
      ? formatThermalMoney(item.total, currency)
      : item.unitPrice != null
        ? formatThermalMoney(item.unitPrice, currency)
        : '';
  return dualColumnBlock(left, right, style, dotsPerLine, lineWidth);
}

async function buildDrawBlocks(
  receipt: ReceiptPayload,
  previewLines: ReceiptPreviewLine[],
  dotsPerLine: number,
  lineWidth: number,
): Promise<TsplDrawBlock[]> {
  const currency = receipt.currency ?? 'LKR';
  const blocks: TsplDrawBlock[] = [];
  let metaIndex = 0;
  let itemIndex = 0;
  const metaCount = receipt.meta?.length ?? 0;

  for (const line of previewLines) {
    if (line.kind === 'spacer') {
      blocks.push(spacerBlock());
      continue;
    }

    if (line.kind === 'logo') {
      const raster = await encodeLogoRaster1Bit(line.url, dotsPerLine);
      if (raster) {
        const h = raster.heightPx + LOGO_GAP_DOTS;
        blocks.push({
          heightDots: h,
          render: (y) => [
            tsplBitmapPart(0, y, raster.bytesPerRow, raster.heightPx, raster.rows),
          ],
        });
      }
      continue;
    }

    if (line.kind === 'qr') {
      const payload = line.text.trim();
      const qrDots = estimateQrBlockDots(payload);
      const caption = fitLineToWidth(payload, lineWidth);
      const capStyle: TsplFontStyle = {
        font: QR_CAPTION_FONT,
        xMul: 1,
        yMul: 1,
        lineHeight: 12,
        centered: true,
      };
      const h = qrDots + LINE_GAP + 12 + LINE_GAP;
      blocks.push({
        heightDots: h,
        render: (y) => {
          const qrX = Math.max(MARGIN_X, Math.floor((dotsPerLine - qrDots) / 2));
          const capX = textXCentered(caption, capStyle, dotsPerLine, lineWidth);
          return [
            tsplQrLine(qrX, y, payload, QR_CELL),
            tsplTextLine(capX, y + qrDots + LINE_GAP, caption, {
              font: QR_CAPTION_FONT,
              xMul: 1,
              yMul: 1,
            }),
          ];
        },
      });
      continue;
    }

    if (line.kind === 'total') {
      const style = STYLE.total!;
      blocks.push(
        dualColumnBlock(
          'TOTAL',
          formatThermalMoney(receipt.total, currency),
          style,
          dotsPerLine,
          lineWidth,
        ),
      );
      continue;
    }

    if (line.kind === 'item') {
      const item = receipt.items?.[itemIndex];
      itemIndex += 1;
      if (item) {
        blocks.push(itemDualColumnBlock(item, currency, STYLE.item!, dotsPerLine, lineWidth));
      } else {
        blocks.push(singleTextBlock(line.text, STYLE.item!, dotsPerLine, lineWidth));
      }
      continue;
    }

    if (line.kind === 'meta') {
      const style = STYLE.meta!;
      if (metaIndex < metaCount) {
        const row = receipt.meta![metaIndex]!;
        metaIndex += 1;
        blocks.push(dualColumnBlock(row.left, row.right, style, dotsPerLine, lineWidth));
      } else {
        blocks.push(singleTextBlock(line.text, style, dotsPerLine, lineWidth));
      }
      continue;
    }

    const style = STYLE[line.kind];
    if (style) {
      blocks.push(singleTextBlock(line.text, style, dotsPerLine, lineWidth));
    }
  }

  return blocks;
}

/**
 * Build a multi-label TSPL job from the same preview layout as {@link layoutReceiptForPreview}.
 * Each physical 48×59.25 mm label gets SIZE + CLS + content + PRINT 1,1.
 */
export async function encodeReceiptPreviewToXpP203aTspl(
  receipt: ReceiptPayload,
): Promise<Uint8Array> {
  const cfg = getXpP203aLabelConfig();
  const headerForPage = (heightMm: number) =>
    buildTsplLabelHeaderLines({ ...cfg, heightMm });
  const lineWidth = getReceiptLineWidthForModel('xp-p203a');
  const dotsPerLine = getReceiptRasterDotsPerLine('xp-p203a');
  const previewLines = layoutReceiptForPreview(receipt, lineWidth);
  const blocks = await buildDrawBlocks(receipt, previewLines, dotsPerLine, lineWidth);

  const maxY = Math.floor(cfg.heightMm * DOTS_PER_MM) - LABEL_BOTTOM_MARGIN_DOTS;
  const pages = paginateDrawBlocks(blocks, maxY);

  const jobLines: TsplJobPart[] = [];
  for (const page of pages) {
    jobLines.push(...headerForPage(cfg.heightMm));
    let y = LABEL_TOP_Y;
    for (const block of page) {
      jobLines.push(...block.render(y));
      y += block.heightDots;
    }
    jobLines.push('PRINT 1,1');
  }

  return buildTsplJob(jobLines);
}
